
"use client";

import clsx from "clsx";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

type MaxWidth =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "full";

type SectionTitleProps = {
  title: React.ReactNode;

  badge?: string | undefined;
  mobileBadge?: string | undefined;
  desktopBadge?: string | undefined;

  highlightRed?: boolean | undefined;

  subtitle?: string | undefined;

  description?: string | string[] | undefined;

  align?: "left" | "center" | "right" | undefined;

  titleMaxWidth?: MaxWidth | undefined;
  descriptionMaxWidth?: MaxWidth | undefined;

  highlightSubtitle?: boolean | undefined;
};

const maxWidthMap: Record<MaxWidth, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

const defaultDescriptionMaxWidth =
  "max-w-full md:max-w-3xl lg:max-w-4xl";

/**
 * Renders normal string content with \n line breaks.
 *
 * Used for subtitle and description.
 *
 * Title accepts React.ReactNode so responsive <br />
 * elements can be controlled manually where required.
 */
const renderTextWithLineBreaks = (text?: string) => {
  if (!text) return null;

  const lines = text.split("\n");

  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line.trim()}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const SectionTitle: React.FC<SectionTitleProps> = ({
  title,
  badge,
  mobileBadge,
  desktopBadge,
  highlightRed = false,
  subtitle,
  description,
  align = "center",
  titleMaxWidth,
  descriptionMaxWidth,
  highlightSubtitle = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  const containerAlignment = clsx(
    "flex flex-col",
    align === "left" && "text-left items-start",
    align === "center" && "text-center items-center",
    align === "right" && "text-right items-end"
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={
        isVisible
          ? { opacity: 1, y: 0 }
          : { opacity: 0, y: 30 }
      }
      transition={{
        duration: 0.6,
        ease: "easeOut",
      }}
      className={containerAlignment}
    >
      {/* Subtitle */}
      {subtitle && (
        <h4
          className={clsx(
            "text-xl sm:text-2xl md:text-3xl font-medium tracking-tight",
            highlightSubtitle
              ? "text-[#d53333]"
              : "text-[#1b1b1b]"
          )}
        >
          {renderTextWithLineBreaks(subtitle)}
        </h4>
      )}

      {/* Badge */}
      {(badge || mobileBadge || desktopBadge) && (
        <div className="mb-4">
          {/* Mobile Badge */}
          <div className="lg:hidden">
            <div className="inline-flex items-center rounded border -skew-x-16 bg-[#272D57] px-4 py-1.5 text-xs 2xl:text-sm text-white">
              <span className="skew-x-16">
                {mobileBadge || badge}
              </span>
            </div>
          </div>

          {/* Desktop Badge */}
          <div className="hidden lg:block">
            <div className="inline-flex items-center rounded border -skew-x-16 bg-[#272D57] px-4 py-1.5 text-xs 2xl:text-sm text-white">
              <span className="skew-x-16">
                {desktopBadge || badge}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      {title && (
        <h2
          className={clsx(
            "text-3xl sm:text-3xl 2xl:text-5xl font-semibold tracking-tight",
            highlightRed
              ? "text-[#d53333]"
              : "text-[#272d57]",
            titleMaxWidth &&
              maxWidthMap[titleMaxWidth]
          )}
        >
          {title}
        </h2>
      )}

      {/* Description */}
      {description && (
        <div className="flex flex-col gap-4 sm:gap-5 lg:gap-6 mt-2 tracking-tight">
          {(Array.isArray(description)
            ? description
            : [description]
          ).map((desc, index) => (
            <p
              key={index}
              className={clsx(
                "text-base lg:text-lg text-[#1b1b1b] leading-tight",
                descriptionMaxWidth
                  ? maxWidthMap[descriptionMaxWidth]
                  : defaultDescriptionMaxWidth,
                align === "left" && "text-left",
                align === "center" && "text-center",
                align === "right" && "text-right"
              )}
            >
              {renderTextWithLineBreaks(desc)}
            </p>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default SectionTitle;
